/**
 * A FALA da etapa "Dia a dia" do onboarding — o formato que o backend lê.
 *
 * ★ **Por que num arquivo próprio, e não dentro do cartão.** Isto não é copy:
 * é CONTRATO. `core/onboarding/prioridades.py` só reconhece a escolha quando a
 * mensagem COMEÇA com o prefixo declarado e traz um título por linha com o
 * marcador na frente — fora disso a escolha some, sem erro nenhum, e o sintoma
 * aparece longe daqui (a memória da IA nunca recebe o que a pessoa marcou). É
 * exatamente a natureza de `utils/perguntas-ao-gestor.js`, que espelha
 * `core/entrega/material.py` pela mesma razão e já custou uma regressão
 * silenciosa (ver §13.1 do `CLAUDE.md` deste módulo).
 *
 * ⚠️ **Espelho MANUAL do backend.** Mudou `PREFIXO_DA_FALA` ou
 * `MARCADOR_DE_ITEM` de um lado, mude do outro no MESMO commit. O teste de
 * junção (`prioridades-do-dia-a-dia.unit.js`, `describeComBackend`) lê os dois
 * fontes e fica vermelho quando divergem — mas ele só roda com o checkout irmão
 * `../aawz-web-backend` presente.
 *
 * ★ **Zero imports, de propósito.** Arquivo folha: quem o consome é o cartão
 * (`ui-registry/cartoes/cartao-onboarding.vue`), que já carrega bastante coisa.
 * Nada aqui lê store, rota ou `this`.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */

/**
 * O começo da mensagem que o cartão envia — a ÂNCORA da leitura do backend.
 *
 * ★ Primeira pessoa porque quem "diz" a frase é o consultor: ela vira a bolha
 * dele na conversa, ao lado de "Continuar" e "Pular esta etapa".
 *
 * ⚠️ Espelho de `core/onboarding/prioridades.PREFIXO_DA_FALA`.
 */
export const PREFIXO_DA_FALA = 'O que mais consome o meu tempo hoje:'

/**
 * Um item por linha, com o marcador na frente.
 *
 * ⚠️ Espelho de `core/onboarding/prioridades.MARCADOR_DE_ITEM`. Lista em prosa
 * ("A, B e C") seria ambígua para ler de volta — título com vírgula partiria
 * em dois.
 */
export const MARCADOR_DE_ITEM = '- '

/**
 * A fala que vai para a conversa, a partir dos TÍTULOS marcados.
 *
 * ⚠️ Os títulos são os que o SERVIDOR mandou em `props.prioridades[].titulo` —
 * nunca uma segunda grafia escrita no front. É por eles que o backend casa a
 * escolha de volta com o catálogo; um título reescrito aqui deixa de casar, em
 * silêncio.
 *
 * @param {Array<string>} titulos Os títulos marcados, na ordem do catálogo.
 * @returns {string} A fala, ou `''` quando nada foi marcado (e aí não há o que
 *   dizer — quem chama trata o vazio).
 * @example montarFalaDasPrioridades(['Compliance e burocracia'])
 *   // → 'O que mais consome o meu tempo hoje:\n- Compliance e burocracia'
 */
export function montarFalaDasPrioridades(titulos) {
  const lista = Array.isArray(titulos) ? titulos : []
  const linhas = []
  for (let i = 0; i < lista.length; i += 1) {
    const titulo = String(lista[i] || '').trim()
    if (titulo) linhas.push(MARCADOR_DE_ITEM + titulo)
  }
  if (!linhas.length) return ''
  return PREFIXO_DA_FALA + '\n' + linhas.join('\n')
}

/**
 * Lê de volta os títulos de uma fala que `montarFalaDasPrioridades` escreveu.
 *
 * É o que devolve a escolha ao cartão depois de um F5 e no aside ([owner,
 * 21/09/2026]): a escolha não tem coluna própria — mora na mensagem seguinte
 * da pessoa, no formato que `core/onboarding/prioridades.py` reconhece. A
 * mesma âncora do backend: sem o PREFIXO no começo, não é uma escolha — uma
 * mensagem comum que cite um dos títulos numa lista com hífen não vira
 * prioridade marcada.
 *
 * @param {*} texto O conteúdo da mensagem `user`.
 * @returns {string[]} Os títulos, na ordem em que foram ditos; `[]` quando o
 *   texto não é a fala das prioridades.
 * @example lerTitulosDaFala('O que mais consome o meu tempo hoje:\n- Reuniões') // → ['Reuniões']
 */
export function lerTitulosDaFala(texto) {
  const conteudo = typeof texto === 'string' ? texto.trim() : ''
  if (!conteudo || conteudo.indexOf(PREFIXO_DA_FALA) !== 0) return []
  return conteudo
    .slice(PREFIXO_DA_FALA.length)
    .split('\n')
    .map((linha) => linha.trim())
    .filter((linha) => linha.indexOf(MARCADOR_DE_ITEM) === 0)
    .map((linha) => linha.slice(MARCADOR_DE_ITEM.length).trim())
    .filter(Boolean)
}
